import { IterableEmbeddedSessionManager } from '@iterable/web-sdk';

import { ITERABLE_PACKAGE_NAME } from '@m/config/env';

import IterableEmbeddedService from './IterableEmbeddedService';

import type { EmbeddedSessionHandler } from './IterableEmbeddedService';

/**
 * Iterable Embedded-Messaging session service.
 *
 * Wraps `IterableEmbeddedSessionManager` from `@iterable/web-sdk` and plugs it
 * into `IterableEmbeddedService` as the impression sink. Embedded sessions are
 * not the same as web sessions — they are explicit windows during which the
 * user is viewing a page that contains embedded placements. The SDK does NOT
 * auto-track this; impressions logged outside an active session are dropped at
 * `endSession` time (no `embeddedSession` payload is sent if no impressions
 * have been recorded).
 *
 * Lifecycle is driven by `useEmbeddedSession`:
 *   start on mount/route entry/visible → impressions accrue → end on
 *   unmount/route exit/hidden/pagehide, which flushes `embeddedSession` to
 *   Iterable.
 */

class EmbeddedSessionService implements EmbeddedSessionHandler {
  private manager: IterableEmbeddedSessionManager | null = null;

  private getManager(): IterableEmbeddedSessionManager {
    if (!this.manager) {
      this.manager = new IterableEmbeddedSessionManager(ITERABLE_PACKAGE_NAME);
    }
    return this.manager;
  }

  /** Start the embedded session. Idempotent — the SDK no-ops if already tracking. */
  startSession(): void {
    this.getManager().startSession();
  }

  /**
   * End the embedded session, flushing any accrued impressions to Iterable as
   * a single `embeddedSession` event. Idempotent — the SDK no-ops if not
   * tracking, and resets internal state so the next `startSession` opens a
   * fresh session id.
   */
  endSession(): Promise<void> {
    if (!this.manager) return Promise.resolve();
    return this.manager.endSession();
  }

  startImpression(messageId: string, placementId: number): void {
    this.getManager().startImpression(messageId, placementId);
  }

  pauseImpression(messageId: string): void {
    this.manager?.pauseImpression(messageId);
  }

  /**
   * Register this service as the impression sink on `IterableEmbeddedService`
   * so `notifyImpression` / `notifyImpressionEnded` flow into the active
   * embedded session. Returns an uninstall function.
   */
  install(): () => void {
    IterableEmbeddedService.setSessionHandler(this);
    return () => {
      IterableEmbeddedService.setSessionHandler(null);
    };
  }
}

const IterableEmbeddedSessionService = new EmbeddedSessionService();

export default IterableEmbeddedSessionService;
